import React, { type ChangeEvent, type MouseEvent, useCallback, useId } from "react";
import { useTranslation } from "react-i18next";

import type { EntityImageManagerProps, GalleryImage } from "./entityImages.types";
import LineIcon from "./LineIcon";

import styles from "./EntityImageManager.module.css";

/**
 * A compact image-gallery editor. Renders thumbnails (click to open the lightbox) plus, when
 * `editable`, an add tile and per-image "set default" / "remove" controls. Presentational only —
 * all state lives in the parent (see useEntityImages). Per-item actions are dispatched via
 * data-attributes so the handlers stay stable across renders.
 */
const EntityImageManager = ({
	images,
	editable,
	busy,
	failed,
	onAddFiles,
	onRemove,
	onSetDefault,
	onOpen
}: EntityImageManagerProps) => {
	const { t } = useTranslation("homePage");
	const inputId = useId();

	const onFilesSelected = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => {
			const files = event.target.files ? Array.from(event.target.files) : [];
			if (files.length > 0) {
				onAddFiles(files);
			}
			// Allow re-selecting the same file after a removal.
			event.target.value = "";
		},
		[onAddFiles]
	);

	const onThumbClick = useCallback(
		(event: MouseEvent<HTMLButtonElement>): void => onOpen(Number(event.currentTarget.dataset.index)),
		[onOpen]
	);
	const onSetDefaultClick = useCallback(
		(event: MouseEvent<HTMLButtonElement>): void => onSetDefault(event.currentTarget.dataset.id ?? ""),
		[onSetDefault]
	);
	const onRemoveClick = useCallback(
		(event: MouseEvent<HTMLButtonElement>): void => onRemove(event.currentTarget.dataset.id ?? ""),
		[onRemove]
	);

	const hasImages = images.length > 0;

	return (
		<div className={styles.gallery}>
			<div className={styles.thumbs}>
				{images.map((image: GalleryImage, index: number) => (
					<div key={image.id} className={styles.thumb}>
						<button
							aria-label={t("editor.imageGallery.view")}
							className={styles.thumbButton}
							data-index={index}
							onClick={onThumbClick}
							type="button">
							<img alt="" className={styles.thumbImage} src={image.url} />
						</button>

						{image.isDefault && (
							<span className={styles.defaultBadge} title={t("editor.imageGallery.default")}>
								<LineIcon name="starFilled" />
							</span>
						)}

						{editable && (
							<div className={styles.thumbActions}>
								{!image.isDefault && (
									<button
										aria-label={t("editor.imageGallery.setDefault")}
										className={styles.thumbAction}
										data-id={image.id}
										disabled={busy}
										onClick={onSetDefaultClick}
										title={t("editor.imageGallery.setDefault")}
										type="button">
										<LineIcon name="star" />
									</button>
								)}
								<button
									aria-label={t("editor.imageGallery.remove")}
									className={`${styles.thumbAction} ${styles.thumbActionDanger}`}
									data-id={image.id}
									disabled={busy}
									onClick={onRemoveClick}
									title={t("editor.imageGallery.remove")}
									type="button">
									<LineIcon name="trash" />
								</button>
							</div>
						)}
					</div>
				))}

				{editable && (
					<label className={styles.addTile} htmlFor={inputId}>
						<LineIcon name="camera" />
						<span>{busy ? t("editor.imageGallery.uploading") : t("editor.imageGallery.add")}</span>
						<input
							accept="image/jpeg,image/png,image/webp,image/gif"
							className={styles.addInput}
							disabled={busy}
							id={inputId}
							onChange={onFilesSelected}
							type="file"
							multiple
						/>
					</label>
				)}
			</div>

			{editable && <p className={styles.hint}>{t("editor.imageGallery.hint")}</p>}
			{failed && <p className={styles.error}>{t("editor.imageGallery.error")}</p>}
			{!hasImages && !editable && <p className={styles.hint}>{t("editor.imageGallery.empty")}</p>}
		</div>
	);
};

export default EntityImageManager;
